import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import { challengesAPI } from '../services/api'
import { 
  MagnifyingGlassIcon, 
  FunnelIcon,
  UserGroupIcon,
  CalendarIcon,
  TrophyIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import ImageWithFallback from '../components/ui/ImageWithFallback'
import Card from '../components/ui/Card'
import { Button } from '../components/ui/button'
import { getCategoryGradient, getPlaceholderImage, getCategoryImage, heroImages } from '../utils/images'
import { format } from 'date-fns'

const difficultyColors = {
  beginner: 'badge-success',
  intermediate: 'badge-warning',
  advanced: 'badge-error'
}

const statusColors = {
  active: 'badge-success',
  upcoming: 'badge-warning',
  completed: 'badge-secondary'
}

export default function ChallengesPage() {
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    difficulty: '',
    status: 'active'
  })

  const { data: challengesData, isLoading, error } = useQuery(
    ['challenges', filters],
    () => challengesAPI.getAll(filters),
    {
      select: (response) => response.data
    }
  )

  const { data: categoriesData } = useQuery(
    'challenge-categories',
    () => challengesAPI.getCategories(),
    {
      select: (response) => response.data.categories
    }
  )

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const filteredChallenges = challengesData?.challenges?.filter(challenge => {
    if (filters.search && !challenge.title.toLowerCase().includes(filters.search.toLowerCase()) &&
        !challenge.description.toLowerCase().includes(filters.search.toLowerCase())) {
      return false
    }
    return true
  }) || []

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error loading challenges</h2>
          <p className="text-gray-600">Please try again later.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-dark-900 min-h-screen particle-bg">
      {/* Artistic Header */}
      <div className="relative aurora-bg border-b border-gold-500/30">
        <div className="absolute inset-0 golden-mesh opacity-15"></div>
        <div className="relative max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center glass-morphism px-6 py-3 rounded-full mb-6">
              <TrophyIcon className="w-5 h-5 text-gold-400 mr-2 animate-sparkle" />
              <span className="text-sm font-medium text-gray-300">Explore Challenges</span>
            </div>
            
            {/* Main Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-white mb-6">
              AI
              <span className="block holographic-text animate-holographic-shift text-stroke-gold">
                Challenges
              </span>
            </h1>
            
            {/* Description */}
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Discover and participate in cutting-edge AI challenges and innovation projects. 
              Compete with innovators worldwide and make a real impact.
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-gold-400 rounded-full mr-2 animate-pulse"></div>
                <span>{challengesData?.total || 0} Total Challenges</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-gold-400 rounded-full mr-2 animate-pulse" style={{animationDelay: '0.5s'}}></div>
                <span>{filteredChallenges.length} Active Challenges</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-gold-400 rounded-full mr-2 animate-pulse" style={{animationDelay: '1s'}}></div>
                <span>Global Competition</span>
              </div>
            </div>
          </div>
          
          {/* Floating Decorative Elements */}
          <div className="absolute top-8 right-8 w-3 h-3 bg-gold-400/30 rounded-full animate-float"></div>
          <div className="absolute bottom-8 left-8 w-2 h-2 bg-gold-500/40 rounded-full animate-pulse"></div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        {/* Enhanced Filters */}
        <Card variant="artistic" className="mb-12">
          <div className="flex items-center mb-6">
            <FunnelIcon className="w-6 h-6 text-gold-400 mr-3 animate-sparkle" />
            <h2 className="text-xl font-display font-bold text-white">Filter Challenges</h2>
            <div className="ml-auto flex items-center space-x-2">
              <div className="w-8 h-0.5 bg-gradient-gold animate-gold-shimmer"></div>
              <div className="w-2 h-2 bg-gold-400 rounded-full animate-pulse"></div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Enhanced Search */}
            <div className="relative group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Search</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 group-focus-within:text-gold-400 transition-colors duration-200" />
                </div>
                <input
                  type="text"
                  placeholder="Search challenges..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="block w-full pl-12 pr-4 py-3 glass-morphism border border-gold-500/20 rounded-xl text-gray-100 placeholder-gray-400 focus:outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-400/20 transition-all duration-300"
                />
                <div className="absolute top-1 right-1 w-1 h-1 bg-gold-400 rounded-full opacity-0 group-focus-within:opacity-100 animate-sparkle transition-opacity duration-300"></div>
              </div>
            </div>

            {/* Enhanced Category Filter */}
            <div className="group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
              <div className="relative">
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="block w-full px-4 py-3 glass-morphism border border-gold-500/20 rounded-xl text-gray-100 focus:outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-400/20 transition-all duration-300 appearance-none cursor-pointer"
                >
                  <option value="">All Categories</option>
                  {categoriesData?.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
                <div className="absolute top-1 right-1 w-1 h-1 bg-gold-400 rounded-full opacity-0 group-focus-within:opacity-100 animate-sparkle transition-opacity duration-300"></div>
              </div>
            </div>

            {/* Enhanced Difficulty Filter */}
            <div className="group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Difficulty</label>
              <div className="relative">
                <select
                  value={filters.difficulty}
                  onChange={(e) => handleFilterChange('difficulty', e.target.value)}
                  className="block w-full px-4 py-3 glass-morphism border border-gold-500/20 rounded-xl text-gray-100 focus:outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-400/20 transition-all duration-300 appearance-none cursor-pointer"
                >
                  <option value="">All Difficulties</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
                <div className="absolute top-1 right-1 w-1 h-1 bg-gold-400 rounded-full opacity-0 group-focus-within:opacity-100 animate-sparkle transition-opacity duration-300"></div>
              </div>
            </div>

            {/* Enhanced Status Filter */}
            <div className="group">
              <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
              <div className="relative">
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="block w-full px-4 py-3 glass-morphism border border-gold-500/20 rounded-xl text-gray-100 focus:outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-400/20 transition-all duration-300 appearance-none cursor-pointer"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                </select>
                <div className="absolute top-1 right-1 w-1 h-1 bg-gold-400 rounded-full opacity-0 group-focus-within:opacity-100 animate-sparkle transition-opacity duration-300"></div>
              </div>
            </div>
          </div>
        </Card>

        {/* Enhanced Results */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="xl" showText />
          </div>
        ) : (
          <>
            {/* Results Header */}
            <div className="mb-8 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="glass-morphism px-4 py-2 rounded-xl">
                  <p className="text-sm text-gray-300">
                    <span className="text-gold-400 font-semibold">{filteredChallenges.length}</span> of <span className="text-gold-400 font-semibold">{challengesData?.total || 0}</span> challenges
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-gold-400 rounded-full animate-pulse"></div>
                  <div className="w-8 h-0.5 bg-gradient-gold animate-gold-shimmer"></div>
                </div>
              </div>
            </div>

            {/* Enhanced Challenge Grid */}
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredChallenges.map((challenge, index) => (
                <Card 
                  key={challenge.id} 
                  variant="artistic" 
                  className="overflow-hidden h-full flex flex-col"
                  style={{animationDelay: `${index * 0.1}s`}}
                >
                  {/* Enhanced Challenge Image */}
                  <div className="relative h-56 overflow-hidden -m-6 mb-6">
                    <ImageWithFallback
                      src={getCategoryImage(challenge.category)}
                      alt={challenge.category}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      fallbackSrc={`data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 200'%3E%3Crect width='400' height='200' fill='%23334155'/%3E%3Ctext x='200' y='100' text-anchor='middle' dy='.3em' fill='%23f59e0b' font-family='Arial, sans-serif' font-size='16'%3E${challenge.category}%3C/text%3E%3C/svg%3E`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/50 to-transparent" />
                    
                    {/* Enhanced Badges */}
                    <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
                      <div className="glass-morphism px-3 py-1 rounded-full">
                        <span className="text-xs font-medium text-gold-400">{challenge.category}</span>
                      </div>
                      <div className={`glass-morphism px-3 py-1 rounded-full border ${
                        challenge.status === 'active' ? 'border-green-500/30' :
                        challenge.status === 'upcoming' ? 'border-yellow-500/30' :
                        'border-gray-500/30'
                      }`}>
                        <span className={`text-xs font-medium ${
                          challenge.status === 'active' ? 'text-green-400' :
                          challenge.status === 'upcoming' ? 'text-yellow-400' :
                          'text-gray-400'
                        }`}>
                          {challenge.status}
                        </span>
                      </div>
                    </div>

                    {/* Difficulty Badge */}
                    <div className="absolute bottom-4 right-4">
                      <div className={`glass-morphism px-3 py-1 rounded-full border ${
                        challenge.difficulty === 'beginner' ? 'border-green-500/30' :
                        challenge.difficulty === 'intermediate' ? 'border-yellow-500/30' :
                        'border-red-500/30'
                      }`}>
                        <span className={`text-xs font-medium ${
                          challenge.difficulty === 'beginner' ? 'text-green-400' :
                          challenge.difficulty === 'intermediate' ? 'text-yellow-400' :
                          'text-red-400'
                        }`}>
                          {challenge.difficulty}
                        </span>
                      </div>
                    </div>

                    {/* Decorative Elements */}
                    <div className="absolute top-2 right-2 w-2 h-2 bg-gold-400 rounded-full animate-sparkle" style={{animationDelay: `${index * 0.3}s`}}></div>
                  </div>

                  {/* Enhanced Content */}
                  <div className="flex-1 flex flex-col">
                    <Card.Header>
                      <Card.Title>
                        <Link 
                          to={`/challenges/${challenge.id}`}
                          className="hover:holographic-text hover:animate-holographic-shift transition-all duration-300"
                        >
                          {challenge.title}
                        </Link>
                      </Card.Title>
                    </Card.Header>
                    
                    <Card.Content>
                      <Card.Description className="mb-6 line-clamp-3">
                        {challenge.description}
                      </Card.Description>

                      {/* Enhanced Stats */}
                      <div className="space-y-3 mb-6">
                        <div className="flex items-center text-sm text-gray-400 group-hover:text-gold-400 transition-colors duration-200">
                          <UserGroupIcon className="h-4 w-4 mr-2" />
                          <span>{challenge.participant_count} participants</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-400 group-hover:text-gold-400 transition-colors duration-200">
                          <CalendarIcon className="h-4 w-4 mr-2" />
                          <span>Ends {format(new Date(challenge.end_date), 'MMM dd')}</span>
                        </div>
                        {challenge.prizes && challenge.prizes.length > 0 && (
                          <div className="flex items-center text-sm text-gold-400 animate-gold-pulse">
                            <TrophyIcon className="h-4 w-4 mr-2" />
                            <span>Prizes available</span>
                          </div>
                        )}
                      </div>
                    </Card.Content>

                    {/* Enhanced Action Buttons */}
                    <Card.Footer>
                      <div className="flex space-x-3">
                        <Button
                          asChild
                          variant="gold"
                          size="md"
                          className="flex-1"
                        >
                          <Link to={`/challenges/${challenge.id}`} className="flex items-center justify-center gap-2">
                            View Details
                            <ArrowRightIcon className="w-4 h-4" />
                          </Link>
                        </Button>
                        {challenge.status === 'active' && (
                          <Button
                            asChild
                            variant="secondary"
                            size="md"
                            className="flex-1"
                          >
                            <Link to={`/challenges/${challenge.id}`}>
                              Join Challenge
                            </Link>
                          </Button>
                        )}
                      </div>
                    </Card.Footer>
                  </div>
                </Card>
              ))}
            </div>

            {/* Enhanced Empty State */}
            {filteredChallenges.length === 0 && (
              <div className="text-center py-20">
                <Card variant="glass" className="max-w-md mx-auto">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-6 neo-morphism rounded-3xl flex items-center justify-center">
                      <FunnelIcon className="w-10 h-10 text-gray-400 animate-float" />
                    </div>
                    <h3 className="text-xl font-display font-semibold text-white mb-4">No challenges found</h3>
                    <p className="text-gray-400 mb-6">
                      Try adjusting your search criteria or filters to discover more challenges.
                    </p>
                    <Button
                      variant="secondary"
                      onClick={() => setFilters({ search: '', category: '', difficulty: '', status: 'active' })}
                    >
                      Clear Filters
                    </Button>
                  </div>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}